import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, Truck, Phone, Mail, Globe, FileText, MapPin, Printer, Users, Key, Send, Eye, EyeOff } from 'lucide-react';
import { apiService } from '../services/api';
import { Supplier } from '../types';
import RoleBasedComponent from './RoleBasedComponent';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import ShippingLabelModal from './ShippingLabelModal';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

const SuppliersPage: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('suppliers', setSearchTerm);
  const [showModal, setShowModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [selectedSupplierForLabel, setSelectedSupplierForLabel] = useState<Supplier | null>(null);
  const [suppliersData, setSuppliersData] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [supplierUsers, setSupplierUsers] = useState<any[]>([]);
  const [visiblePasswords, setVisiblePasswords] = useState<Record<number, boolean>>({});
  const [userFormData, setUserFormData] = useState({
    username: '',
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [supplierData, setSupplierData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    tax_number: '',
    provides_technical_service: false,
    service_categories: [] as string[]
  });

  const serviceCategories = [
    'Bilgisayar',
    'Bilgisayar Ekipmanları',
    'Yazıcı',
    'Endüstriyel Terminal',
    'Endüstriyel Yazıcı',
    'Cep Telefonu',
    'Cep Telefonu Aksesuarı',
    'Sarf Malzeme',
    'Teknik Servis Hizmeti',
    'Güvenlik Kamera Sistemleri',
    'Network Ekipmanları',
    'Sunucu ve Depolama Üniteleri',
    'Sistem Destek Hizmetleri',
    'Kiralama Hizmetleri'
  ];

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const suppliersResponse = await apiService.getSuppliers();
      const suppliers = suppliersResponse.data || suppliersResponse;
      setSuppliersData(Array.isArray(suppliers) ? suppliers : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('suppliers.errLoad'));
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = Array.isArray(suppliersData) ? suppliersData.filter(supplier =>
    String(supplier.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(supplier.contact_person || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(supplier.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    
    // Format phone number for editing
    let formattedPhone = supplier.phone;
    if (formattedPhone && !formattedPhone.startsWith('+90')) {
      const digits = formattedPhone.replace(/\D/g, '');
      if (digits.length === 10) {
        formattedPhone = '+90 ' + digits.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
      }
    }
    
    setSupplierData({
      name: supplier.name,
      contact_person: supplier.contact_person,
      phone: formattedPhone,
      email: supplier.email,
      address: supplier.address,
      website: supplier.website || '',
      tax_number: supplier.tax_number || '',
      provides_technical_service: supplier.provides_technical_service || false,
      service_categories: (() => {
        try {
          return supplier.service_categories ? (typeof supplier.service_categories === 'string' ? JSON.parse(supplier.service_categories) : supplier.service_categories) : [];
        } catch (e) {
          return [];
        }
      })()
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingSupplier(null);
    setSupplierData({
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      website: '',
      tax_number: '',
      provides_technical_service: false,
      service_categories: []
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!supplierData.name || !supplierData.contact_person || !supplierData.phone || !supplierData.address) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('suppliers.errRequired'));
      return;
    }

    try {
      // Teknik servis hizmeti kategorisi seçilmişse provides_technical_service true yap
      const dataToSend = {
        ...supplierData,
        provides_technical_service: supplierData.service_categories.includes('Teknik Servis Hizmeti')
      };

      if (editingSupplier) {
        // Update existing supplier
        await apiService.updateSupplier(editingSupplier.id, dataToSend);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('suppliers.successUpdated'));
      } else {
        // Add new supplier
        await apiService.createSupplier(dataToSend);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('suppliers.successCreated'));
      }
      
      // Reload data
      await loadData();
      setShowModal(false);
      setSupplierData({
        name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        website: '',
        tax_number: '',
        provides_technical_service: false,
        service_categories: []
      });
    } catch (error: any) {
      console.error('Error saving supplier:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('suppliers.errSave'));
    }
  };

  const handleDelete = async (id: number) => {
    const supplierToDelete = suppliersData.find(s => s.id === id);
    if (!supplierToDelete) return;
    
    if (confirm(t('suppliers.confirmDelete'))) {
      try {
        await apiService.deleteSupplier(id);
        await loadData();
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('suppliers.successDeleted'));
      } catch (error: any) {
        console.error('Error deleting supplier:', error);
        showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('suppliers.errDelete'));
      }
    }
  };

  const handleManageUsers = async (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/suppliers/${supplier.id}/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      if (response.ok) {
        const users = await response.json();
        setSupplierUsers(users);
      }
    } catch (error) {
      console.error('Error loading supplier users:', error);
    }
    setShowUserModal(true);
  };

  const handleCreateUser = async () => {
    if (!userFormData.username || !userFormData.name || !userFormData.password) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('suppliers.errRequired'));
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/suppliers/${selectedSupplier?.id}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...userFormData,
          supplier_id: selectedSupplier?.id
        })
      });

      if (response.ok) {
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('suppliers.userCreated'));
        setUserFormData({ username: '', name: '', email: '', phone: '', password: '' });
        handleManageUsers(selectedSupplier!);
      } else {
        const error = await response.json();
        showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.error || t('suppliers.errUserCreate'));
      }
    } catch (error) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('suppliers.errUserCreate'));
    }
  };

  const handleResetPassword = async (userId: number) => {
    if (!confirm(t('suppliers.confirmResetPassword'))) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/suppliers/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', `${language === 'en' ? 'New password:' : 'Yeni şifre:'} ${result.newPassword}`);
      }
    } catch (error) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('suppliers.errResetPassword'));
    }
  };

  const handleSendCredentials = async (userId: number) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/suppliers/users/${userId}/send-credentials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('suppliers.successEmailSent'));
      }
    } catch (error) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('suppliers.errEmailSend'));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('suppliers.title')}</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{t('suppliers.subtitle')}</p>
        </div>
        <RoleBasedComponent requiredPermissions={['suppliers.create']}>
          <button
            onClick={handleAdd}
            className="flex items-center justify-center px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors min-h-[44px] w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('suppliers.newSupplier')}
          </button>
        </RoleBasedComponent>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder={t('suppliers.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent w-full text-base min-h-[44px]"
        />
      </div>

      {/* Suppliers Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filteredSuppliers.map((supplier) => (
          <div
            key={supplier.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center min-w-0 flex-1">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-gray-100 ml-2 sm:ml-3 truncate">
                  {supplier.name}
                </h3>
              </div>
              <div className="flex space-x-1 sm:space-x-2 flex-shrink-0">
                {supplier.provides_technical_service && (
                  <button
                    onClick={() => handleManageUsers(supplier)}
                    className="p-1 sm:p-2 text-gray-400 hover:text-purple-600 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
                    title="Kullanıcı Yönetimi"
                  >
                    <Users className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedSupplierForLabel(supplier);
                    setShowLabelModal(true);
                  }}
                  className="p-1 sm:p-2 text-gray-400 hover:text-green-600 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
                  title="Kargo Etiketi Yazdır"
                >
                  <Printer className="w-4 h-4" />
                </button>
                <RoleBasedComponent requiredPermissions={['suppliers.update']}>
                  <button
                    onClick={() => handleEdit(supplier)}
                    className="p-1 sm:p-2 text-gray-400 hover:text-primary transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </RoleBasedComponent>
                <RoleBasedComponent requiredPermissions={['suppliers.delete']}>
                  <button
                    onClick={() => handleDelete(supplier.id)}
                    className="p-1 sm:p-2 text-gray-400 hover:text-red-600 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </RoleBasedComponent>
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-2 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{supplier.contact_person}</p>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{supplier.phone}</p>
                </div>
              </div>

              <div className="flex items-center">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-2 flex-shrink-0" />
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{supplier.email}</p>
              </div>

              <div className="flex items-start">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{supplier.address}</p>
              </div>

              {supplier.website && (
                <div className="flex items-center">
                  <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-2 flex-shrink-0" />
                  <a
                    href={supplier.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm text-primary hover:text-primary-dark truncate"
                  >
                    {supplier.website}
                  </a>
                </div>
              )}

              {supplier.tax_number && (
                <div className="flex items-center">
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-2 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                    {t('suppliers.taxNo')} {supplier.tax_number}
                  </p>
                </div>
              )}
              
              {(() => {
                let categories = [];
                try {
                  categories = supplier.service_categories ? (typeof supplier.service_categories === 'string' ? JSON.parse(supplier.service_categories) : supplier.service_categories) : [];
                } catch (e) {
                  categories = [];
                }
                return categories.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('suppliers.serviceCategories')}</p>
                    <div className="flex flex-wrap gap-1">
                      {categories.slice(0, 2).map((category: string, index: number) => (
                        <span key={index} className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800 truncate max-w-[120px]">
                          {category}
                        </span>
                      ))}
                      {categories.length > 2 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">+{categories.length - 2} {language === 'en' ? 'more' : 'daha'}</span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                {t('suppliers.createdAt')} {new Date(supplier.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto sm:rounded-lg rounded-t-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingSupplier ? t('suppliers.editTitle') : t('suppliers.newTitle')}
              </h2>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('suppliers.labelName')}
                  </label>
                  <input
                    type="text"
                    value={supplierData.name}
                    onChange={(e) => setSupplierData({...supplierData, name: e.target.value})}
                    className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base min-h-[44px]"
                    placeholder="Örn: TechnoMarket A.Ş."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('suppliers.labelContact')}
                  </label>
                  <input
                    type="text"
                    value={supplierData.contact_person}
                    onChange={(e) => setSupplierData({...supplierData, contact_person: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Mehmet Özkan"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('suppliers.labelPhone')}
                  </label>
                  <input
                    type="text"
                    value={supplierData.phone}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length > 0) {
                        if (value.startsWith('90')) {
                          value = value.substring(2);
                        }
                        if (value.length <= 10) {
                          if (value.length >= 7) {
                            value = value.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
                          } else if (value.length >= 4) {
                            value = value.replace(/(\d{3})(\d{3})/, '$1 $2');
                          }
                          value = '+90 ' + value;
                        }
                      }
                      setSupplierData({...supplierData, phone: value});
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="+90 212 555 01 01"
                    maxLength={17}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('suppliers.labelEmail')}
                  </label>
                  <input
                    type="email"
                    value={supplierData.email}
                    onChange={(e) => setSupplierData({...supplierData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="mehmet@technomarket.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('suppliers.labelWebsite')}
                  </label>
                  <input
                    type="url"
                    value={supplierData.website}
                    onChange={(e) => setSupplierData({...supplierData, website: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="https://www.technomarket.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('suppliers.labelTaxNo')}
                  </label>
                  <input
                    type="text"
                    value={supplierData.tax_number}
                    onChange={(e) => setSupplierData({...supplierData, tax_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="1234567890"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('suppliers.labelAddress')}
                </label>
                <textarea
                  value={supplierData.address}
                  onChange={(e) => setSupplierData({...supplierData, address: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Tam adres bilgisi"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('suppliers.labelCategories')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {serviceCategories.map(category => (
                    <label key={category} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={supplierData.service_categories.includes(category)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSupplierData({
                              ...supplierData,
                              service_categories: [...supplierData.service_categories, category]
                            });
                          } else {
                            setSupplierData({
                              ...supplierData,
                              service_categories: supplierData.service_categories.filter(c => c !== category)
                            });
                          }
                        }}
                        className="rounded border-gray-300 dark:border-gray-600 text-orange-600 shadow-sm focus:border-orange-300 focus:ring focus:ring-orange-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{category}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('suppliers.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
              >
                {editingSupplier ? t('suppliers.update') : t('suppliers.save')}
              </button>
            </div>
          </div>
        </Modal>
      
      {/* User Management Modal */}
      <Modal isOpen={showUserModal} onClose={() => setShowUserModal(false)}>
          {selectedSupplier && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedSupplier.name} - {t('suppliers.userManagement')}
              </h2>
            </div>
            
            <div className="p-6">
              {/* Create User Form */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('suppliers.createUser')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('suppliers.labelUsername')}
                    </label>
                    <input
                      type="text"
                      value={userFormData.username}
                      onChange={(e) => setUserFormData({...userFormData, username: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="supplier_user"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('suppliers.labelFullName')}
                    </label>
                    <input
                      type="text"
                      value={userFormData.name}
                      onChange={(e) => setUserFormData({...userFormData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Ahmet Yılmaz"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('suppliers.labelEmail2')}
                    </label>
                    <input
                      type="email"
                      value={userFormData.email}
                      onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="ahmet@supplier.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('suppliers.labelPhone2')}
                    </label>
                    <input
                      type="text"
                      value={userFormData.phone}
                      onChange={(e) => setUserFormData({...userFormData, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="+90 555 123 45 67"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('suppliers.labelPassword')}
                    </label>
                    <input
                      type="password"
                      value={userFormData.password}
                      onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Güvenli şifre"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={handleCreateUser}
                      className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                    >
                      <Plus className="w-4 h-4 mr-2 inline" />
                      {t('suppliers.createUserBtn')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Users List */}
              <div>
                <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('suppliers.existingUsers')}</h3>
                {supplierUsers.length > 0 ? (
                  <div className="space-y-3">
                    {supplierUsers.map((user) => (
                      <div key={user.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-gray-900 dark:text-gray-100">{user.name}</p>
                              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {user.is_active ? t('suppliers.statusActive') : t('suppliers.statusInactive')}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">@{user.username}</p>
                            {user.email && <p className="text-sm text-gray-600 dark:text-gray-400">{user.email}</p>}
                            {user.phone && <p className="text-sm text-gray-600 dark:text-gray-400">{user.phone}</p>}

                            {/* Parola */}
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400">{t('suppliers.passwordLabel')}</span>
                              {user.plain_password ? (
                                <>
                                  <span className="text-sm font-mono text-gray-800 dark:text-gray-200">
                                    {visiblePasswords[user.id] ? user.plain_password : '••••••••'}
                                  </span>
                                  <button
                                    onClick={() => setVisiblePasswords(prev => ({ ...prev, [user.id]: !prev[user.id] }))}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                    title={visiblePasswords[user.id] ? 'Gizle' : 'Göster'}
                                  >
                                    {visiblePasswords[user.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                  </button>
                                </>
                              ) : (
                                <span className="text-xs text-gray-400 italic">{t('suppliers.unknownPassword')}</span>
                              )}
                            </div>

                            {user.last_login && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {t('suppliers.lastLogin')} {new Date(user.last_login).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                              </p>
                            )}
                          </div>
                          <div className="flex space-x-2 flex-shrink-0">
                            <button
                              onClick={() => handleResetPassword(user.id)}
                              className="p-2 text-orange-600 hover:text-orange-800"
                              title="Şifre Sıfırla"
                            >
                              <Key className="w-4 h-4" />
                            </button>
                            {user.email && (
                              <button
                                onClick={() => handleSendCredentials(user.id)}
                                className="p-2 text-primary hover:text-primary-dark"
                                title="Giriş Bilgilerini Gönder"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>{t('suppliers.noUsers')}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setSelectedSupplier(null);
                  setUserFormData({ username: '', name: '', email: '', phone: '', password: '' });
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
              >
                {t('suppliers.close')}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Shipping Label Modal */}
      <ShippingLabelModal
        isOpen={showLabelModal}
        onClose={() => {
          setShowLabelModal(false);
          setSelectedSupplierForLabel(null);
        }}
        supplier={selectedSupplierForLabel}
      />
    </div>
  );
};

export default SuppliersPage;